<template>
  <div class="relative z-10" aria-labelledby="modal-title" role="dialog" aria-modal="true">
    <!-- Overlay -->
    <div class="fixed inset-0 bg-gray-900 bg-opacity-75 transition-opacity"></div>
    <!-- Modal Container -->
    <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
      <div class="flex min-h-full items-center justify-center p-4">
        <div class="relative transform overflow-hidden rounded-lg bg-gray-800 text-white shadow-xl transition-all sm:max-w-3xl w-full">
          <div class="p-6">
            <!-- Header -->
            <div class="flex justify-between items-center mb-6">
              <h3 class="text-xl font-semibold text-indigo-300">แก้ไขวิญญาณ</h3>
              <button @click="cancelModal" class="text-gray-400 hover:text-white">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <!-- Tabs Navigation -->
            <div class="flex space-x-4 mb-6">
              <button
                @click="currentTab = 'basic'"
                :class="{
                  'px-4 py-2 rounded-lg text-white': true,
                  'bg-indigo-600 hover:bg-indigo-700': currentTab === 'basic',
                  'bg-gray-600 hover:bg-gray-700': currentTab !== 'basic'
                }"
              >
                ข้อมูลพื้นฐาน
              </button>
              <button
                v-if="ghost.type === 'boss'"
                @click="currentTab = 'drops'"
                :class="{
                  'px-4 py-2 rounded-lg text-white': true,
                  'bg-indigo-600 hover:bg-indigo-700': currentTab === 'drops',
                  'bg-gray-600 hover:bg-gray-700': currentTab !== 'drops'
                }"
              >
                ไอเทมที่ดรอป
              </button>
            </div>
            <!-- Form -->
            <form @submit.prevent="updateGhost">
              <!-- Basic Info and Stats Section -->
              <div v-if="currentTab === 'basic'" class="space-y-4 mb-6">
                <h4 class="text-lg font-medium text-gray-300">ข้อมูลพื้นฐาน</h4>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label class="block text-sm font-medium mb-1 text-gray-300">ชื่อวิญญาณ</label>
                    <input
                      v-model="ghost.name"
                      type="text"
                      placeholder="เช่น วิญญาณธรรมดา"
                      class="w-full px-3 py-2 bg-gray-700 rounded-lg text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      required
                    />
                  </div>
                  <div>
                    <label class="block text-sm font-medium mb-1 text-gray-300">ลิงก์รูปภาพ</label>
                    <input
                      v-model="ghost.imageUrl"
                      type="url"
                      placeholder="https://example.com/image.jpg"
                      class="w-full px-3 py-2 bg-gray-700 rounded-lg text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      required
                    />
                    <img v-if="ghost.imageUrl" :src="ghost.imageUrl" alt="ตัวอย่างรูปภาพ" class="mt-2 w-32 h-32 object-cover rounded-lg border border-gray-600" />
                  </div>
                  <div>
                    <label class="block text-sm font-medium mb-1 text-gray-300">เลเวลของวิญญาณ</label>
                    <input
                      v-model.number="ghost.level"
                      type="number"
                      min="1"
                      max="1000"
                      class="w-full px-3 py-2 bg-gray-700 rounded-lg text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      required
                    />
                  </div>
                  <div>
                    <label class="block text-sm font-medium mb-1 text-gray-300">ประเภทวิญญาณ</label>
                    <select
                      v-model="ghost.type"
                      class="w-full px-3 py-2 bg-gray-700 rounded-lg text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      required
                    >
                      <option value="normal">ทั่วไป</option>
                      <option value="boss">บอส</option>
                    </select>
                  </div>
                </div>
                <!-- Stats Section -->
                <h4 class="text-lg font-medium text-gray-300 mt-6">ค่าสถานะ</h4>
                <div class="grid grid-cols-2 sm:grid-cols-6 gap-4">
                  <div>
                    <label class="block text-sm font-medium mb-1 text-gray-300">Stamina</label>
                    <input
                      v-model.number="ghost.stats.stamina"
                      type="number"
                      min="0"
                      class="w-full px-3 py-2 bg-gray-700 rounded-lg text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label class="block text-sm font-medium mb-1 text-gray-300">Strength</label>
                    <input
                      v-model.number="ghost.stats.str"
                      type="number"
                      min="0"
                      class="w-full px-3 py-2 bg-gray-700 rounded-lg text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label class="block text-sm font-medium mb-1 text-gray-300">Agility</label>
                    <input
                      v-model.number="ghost.stats.agi"
                      type="number"
                      min="0"
                      class="w-full px-3 py-2 bg-gray-700 rounded-lg text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label class="block text-sm font-medium mb-1 text-gray-300">Intelligence</label>
                    <input
                      v-model.number="ghost.stats.int"
                      type="number"
                      min="0"
                      class="w-full px-3 py-2 bg-gray-700 rounded-lg text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label class="block text-sm font-medium mb-1 text-gray-300">Defense</label>
                    <input
                      v-model.number="ghost.stats.defense"
                      type="number"
                      min="0"
                      class="w-full px-3 py-2 bg-gray-700 rounded-lg text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label class="block text-sm font-medium mb-1 text-gray-300">Luck</label>
                    <input
                      v-model.number="ghost.stats.luck"
                      type="number"
                      min="0"
                      class="w-full px-3 py-2 bg-gray-700 rounded-lg text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>
              </div>
              <!-- Drops Section (only for boss) -->
              <div v-if="currentTab === 'drops' && ghost.type === 'boss'" class="space-y-4 mb-6">
                <h4 class="text-lg font-medium text-gray-300">ไอเทมที่ดรอป</h4>
                <div class="max-h-80 overflow-y-auto border border-gray-600 rounded-lg p-4 bg-gray-700">
                  <div v-for="(drop, index) in ghost.drops" :key="index" class="grid grid-cols-1 sm:grid-cols-12 gap-4 mb-4 items-center">
                    <div class="sm:col-span-4">
                      <label class="block text-sm font-medium mb-1 text-gray-300">ชื่อไอเทม</label>
                      <input
                        v-model="drop.itemName"
                        type="text"
                        placeholder="ชื่อไอเทม"
                        class="w-full px-3 py-2 bg-gray-800 rounded-lg text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        required
                      />
                    </div>
                    <div class="sm:col-span-3">
                      <label class="block text-sm font-medium mb-1 text-gray-300">โอกาสดรอป (1-10000)</label>
                      <input
                        v-model.number="drop.dropChance"
                        type="number"
                        min="1"
                        max="10000"
                        class="w-full px-3 py-2 bg-gray-800 rounded-lg text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        required
                      />
                    </div>
                    <div class="sm:col-span-3 flex items-center">
                      <label class="block text-sm font-medium text-gray-300">
                        <input type="checkbox" v-model="drop.transferable" class="mr-2 text-indigo-600 focus:ring-indigo-500" />
                        สามารถโอนได้
                      </label>
                    </div>
                    <div class="sm:col-span-2 flex items-end space-x-2">
                      <button
                        @click="moveUp(index)"
                        type="button"
                        :disabled="index === 0"
                        class="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
                      >
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7" />
                        </svg>
                      </button>
                      <button
                        @click="moveDown(index)"
                        type="button"
                        :disabled="index === ghost.drops.length - 1"
                        class="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
                      >
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      <button
                        @click="removeDrop(index)"
                        type="button"
                        class="p-2 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 transition duration-200"
                      >
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  <div v-if="ghost.drops.length === 0" class="text-center text-gray-400">ไม่มีไอเทมที่ดรอป yet. คลิก "เพิ่มไอเทม" เพื่อเพิ่ม.</div>
                </div>
                <button
                  @click="addDrop"
                  type="button"
                  class="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 transition duration-200"
                >
                  เพิ่มไอเทม
                </button>
              </div>
              <!-- Action Buttons -->
              <div class="flex justify-end gap-3">
                <button
                  type="submit"
                  class="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition duration-200"
                >
                  บันทึกการแก้ไข
                </button>
                <button
                  @click="deleteGhost"
                  type="button"
                  class="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 transition duration-200"
                >
                  ลบวิญญาณ
                </button>
                <button
                  @click="cancelModal"
                  type="button"
                  class="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 transition duration-200"
                >
                  ยกเลิก
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { ref, watch, onMounted, onUnmounted } from 'vue';
import { projectFirestore } from '@/firebase/config';
export default {
  props: ['ghost'],
  setup(props, context) {
    const ghost = ref({
      ...props.ghost,
      stats: {
        stamina: props.ghost.stats?.stamina || 0,
        str: props.ghost.stats?.str || 0,
        agi: props.ghost.stats?.agi || 0,
        int: props.ghost.stats?.int || 0,
        defense: props.ghost.stats?.defense || 0,
        luck: props.ghost.stats?.luck || 0,
      },
      type: props.ghost.type || 'normal',
      drops: props.ghost.drops || [],
      itemNames: props.ghost.drops?.map(drop => drop.itemName) || [],
    });
    const currentTab = ref('basic');
    // Watch for changes in ghost type to manage drops tab
    watch(() => ghost.value.type, () => {
      if (ghost.value.type !== 'boss') {
        ghost.value.drops = [];
        ghost.value.itemNames = [];
        if (currentTab.value === 'drops') {
          currentTab.value = 'basic';
        }
      }
    });
    // Watch for changes in drops to update itemNames
    watch(() => ghost.value.drops, (newDrops) => {
      ghost.value.itemNames = newDrops.map(drop => drop.itemName);
    }, { deep: true });
    const addDrop = () => {
      ghost.value.drops.push({
        itemName: '',
        dropChance: 1,
        transferable: false,
      });
    };
    const removeDrop = (index) => {
      ghost.value.drops.splice(index, 1);
    };
    const moveUp = (index) => {
      if (index > 0) {
        const temp = ghost.value.drops[index];
        ghost.value.drops.splice(index, 1);
        ghost.value.drops.splice(index - 1, 0, temp);
      }
    };
    const moveDown = (index) => {
      if (index < ghost.value.drops.length - 1) {
        const temp = ghost.value.drops[index];
        ghost.value.drops.splice(index, 1);
        ghost.value.drops.splice(index + 1, 0, temp);
      }
    };
    const cancelModal = () => {
      context.emit('cancelModal');
    };
    const updateGhost = async () => {
      try {
        const updatedData = {
          name: ghost.value.name,
          imageUrl: ghost.value.imageUrl,
          level: ghost.value.level,
          type: ghost.value.type,
          stats: ghost.value.stats,
          createdAt: ghost.value.createdAt,
          damageTaken: ghost.value.damageTaken || 0,
          itemNames: ghost.value.itemNames,
        };
        if (ghost.value.type === 'boss') {
          updatedData.drops = ghost.value.drops;
        }
        // Delete old document
        await projectFirestore.collection('gameGhosts').doc(ghost.value.id).delete();
        // Create new document with ghost name as ID
        await projectFirestore.collection('gameGhosts').doc(ghost.value.name).set(updatedData);
        context.emit('ghostUpdated');
        cancelModal();
      } catch (error) {
        console.error('Error updating ghost:', error);
      }
    };
    const deleteGhost = async () => {
      if (confirm('คุณแน่ใจหรือไม่ว่าต้องการลบวิญญาณนี้? การกระทำนี้ไม่สามารถย้อนกลับได้')) {
        try {
          await projectFirestore.collection('gameGhosts').doc(ghost.value.id).delete();
          context.emit('ghostDeleted');
          cancelModal();
        } catch (error) {
          console.error('Error deleting ghost:', error);
        }
      }
    };
    const handleKeydown = (event) => {
      if (event.key === 'Escape') {
        cancelModal();
      }
    };
    onMounted(() => {
      window.addEventListener('keydown', handleKeydown);
    });
    onUnmounted(() => {
      window.removeEventListener('keydown', handleKeydown);
    });
    return {
      ghost,
      currentTab,
      addDrop,
      removeDrop,
      moveUp,
      moveDown,
      cancelModal,
      updateGhost,
      deleteGhost,
    };
  },
};
</script>